import { MutableRefObject, useCallback, useMemo } from 'react';

import {
  GridApi,
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnVisibilityModel,
  GridPinnedColumnFields,
} from '@mui/x-data-grid-premium';

import {
  applyOrder,
  applyStoredWidths,
  extractColumnOrderFromApi,
  pinActionsColumn,
} from './headersApiAdapter';
import useColumnWidthPersistence from './useColumnWidthPersistence';
import useHeadersBackendSync from './useHeadersBackendSync';

export interface UseTableHeadersPersistenceParams {
  headersRequest: { pageType: string; resourceId?: string } | undefined;
  processedColumns: GridColDef[];
  effectiveColumnVisibilityModel: Record<string, boolean>;
  apiRef: MutableRefObject<GridApi>;
  onColumnVisibilityModelChange?: (model: GridColumnVisibilityModel) => void;
  onColumnOrderChange?: (params: GridColumnOrderChangeParams) => void;
  groupByField?: string | null;
  onGroupByChange?: (field: string | null) => void;
  pinnedColumns?: GridPinnedColumnFields;
  onPinnedColumnsChange?: (pinned: GridPinnedColumnFields) => void;
  mandatoryPinnedColumns?: GridPinnedColumnFields;
}

const useTableHeadersPersistence = ({
  headersRequest,
  processedColumns,
  effectiveColumnVisibilityModel,
  apiRef,
  onColumnVisibilityModelChange,
  onColumnOrderChange,
  groupByField,
  onGroupByChange,
  pinnedColumns,
  onPinnedColumnsChange,
  mandatoryPinnedColumns,
}: UseTableHeadersPersistenceParams) => {
  const pageType = headersRequest?.pageType;
  const resourceId = headersRequest?.resourceId;

  const { storedWidths, handleColumnWidthChange } = useColumnWidthPersistence({
    pageType,
    resourceId,
    apiRef,
  });

  const { savedOrder, dragOrder, setDragOrder, isHeadersLoading, scheduleSave } =
    useHeadersBackendSync({
      headersRequest,
      processedColumns,
      effectiveColumnVisibilityModel,
      apiRef,
      onColumnVisibilityModelChange,
      groupByField,
      onGroupByChange,
      pinnedColumns,
      onPinnedColumnsChange,
      mandatoryPinnedColumns,
    });

  const orderedColumns = useMemo(() => {
    const reordered = applyOrder(processedColumns, dragOrder ?? savedOrder);
    const pinned = pinActionsColumn(reordered);
    return applyStoredWidths(pinned, storedWidths);
  }, [processedColumns, savedOrder, dragOrder, storedWidths]);

  const handleColumnOrderChange = useCallback(
    (params: GridColumnOrderChangeParams) => {
      onColumnOrderChange?.(params);
      const newOrder = extractColumnOrderFromApi(apiRef, processedColumns);
      setDragOrder(newOrder);
      if (headersRequest) scheduleSave();
    },
    [onColumnOrderChange, apiRef, processedColumns, setDragOrder, headersRequest, scheduleSave],
  );

  const handleVisibilityChange = useCallback(
    (model: GridColumnVisibilityModel) => onColumnVisibilityModelChange?.(model),
    [onColumnVisibilityModelChange],
  );

  const getColumnOrder = useCallback(
    () => dragOrder ?? savedOrder ?? processedColumns.map((col) => col.field),
    [dragOrder, savedOrder, processedColumns],
  );

  return {
    orderedColumns,
    isHeadersLoading,
    getColumnOrder,
    handleColumnOrderChange,
    handleVisibilityChange,
    handleColumnWidthChange,
  };
};

export default useTableHeadersPersistence;
